/**
 * The page's loader for the shared scene's raw RGBA atlases: Vite copies the
 * files from packages/putt-scene/assets into the build (hashed), and the page
 * fetches them on demand, or ahead of time from the biome preload; either
 * way a key is fetched once per page and shared.
 */
import type { PuttSceneAssets, RawRgbaAssetKey } from '../../../packages/putt-scene/src/assets';

const ASSET_URLS: Readonly<Record<RawRgbaAssetKey, string>> = {
  'foliage-branches-v1': new URL('../../../packages/putt-scene/assets/foliage-branches-v1.rgba', import.meta.url).href,
  'foliage-branches-v1-reduced': new URL('../../../packages/putt-scene/assets/foliage-branches-v1-reduced.rgba', import.meta.url).href,
  'clubhouses-cedar-island-desert-links': new URL('../../../packages/putt-scene/assets/clubhouses-cedar-island-desert-links.rgba', import.meta.url).href,
  'clubhouses-city-alpine-pine-autumn': new URL('../../../packages/putt-scene/assets/clubhouses-city-alpine-pine-autumn.rgba', import.meta.url).href,
  'clubhouses-twilight-storm': new URL('../../../packages/putt-scene/assets/clubhouses-twilight-storm.rgba', import.meta.url).href,
};

const cache = new Map<RawRgbaAssetKey, Promise<Uint8Array<ArrayBuffer>>>();

/** Bytes the page has already fetched: the preload (scenePreload.ts) fills
 *  the cache while the page is idle so the scene's own request hits it. */
export function rawRgbaCached(key: RawRgbaAssetKey): boolean {
  return cache.has(key);
}

export const stageAssets: PuttSceneAssets = {
  loadRawRgba(key) {
    let pending = cache.get(key);
    if (!pending) {
      pending = fetch(ASSET_URLS[key]).then(async (response) => {
        if (!response.ok) throw new Error(`${key}: HTTP ${response.status}`);
        return new Uint8Array(await response.arrayBuffer());
      });
      pending.catch(() => cache.delete(key));
      cache.set(key, pending);
    }
    return pending;
  },
};
